import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ArrowRight, Shield, Activity } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrganizationPage() {
  const { customId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    loadOrganization();
    checkSubscription();
  }, [customId]);

  const loadOrganization = async () => {
    try {
      const response = await axios.get(`${API}/organizations/${customId}`);
      setOrganization(response.data);
    } catch (error) {
      console.error('Error loading organization:', error);
      toast.error('Organization not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/organizations/${customId}/has-subscription`);
      setHasSubscription(response.data.has_subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      toast.info('Please create an account to search healthcare costs');
      // Store org ID in localStorage so we can return after login
      localStorage.setItem('org_custom_id', customId);
      navigate('/auth');
    } else {
      toast.error('Please enter a procedure name or CPT code');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  const primaryColor = organization.branding?.primary_color || '#10b981';

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Organization Logo/Name */}
            <div className="flex items-center gap-3">
              {organization.branding?.logo_url ? (
                <img 
                  src={organization.branding.logo_url}
                  alt={`${organization.organization_name} Logo`}
                  className="h-12 w-auto"
                />
              ) : (
                <div 
                  className="px-6 py-2 rounded-xl font-bold text-white text-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  {organization.organization_name}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/auth')}
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                Log In
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20" style={{ 
        background: `linear-gradient(to right, ${primaryColor}, ${adjustColor(primaryColor, 20)})`
      }}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <Shield className="w-5 h-5" />
            <span className="text-sm font-semibold">Powered by Healthcare Dollar</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            {organization.organization_name}
          </h1>
          
          <p className="text-xl md:text-2xl mb-6 font-light">
            Know Your Healthcare Costs Before Your Visit
          </p>
          
          <p className="text-lg mb-8 opacity-90">
            Get personalized cost estimates for your procedures based on your insurance plan
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: primaryColor }}>
              Search Healthcare Costs
            </h2>
            <p className="text-lg text-gray-600">
              Find cost estimates for procedures at {organization.organization_name}
            </p>
          </div>

          {/* Search Form */}
          <Card className="border-2 shadow-xl" style={{ borderColor: primaryColor }}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Search className="w-6 h-6" style={{ color: primaryColor }} />
                Get Your Cost Estimate
              </CardTitle>
              <CardDescription>
                Enter a procedure name or CPT code to see your estimated out-of-pocket costs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="e.g., Knee Replacement or CPT 27447"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 text-lg border-2"
                    style={{ borderColor: `${primaryColor}40` }}
                  />
                </div>
                <Button
                  type="submit"
                  size="lg"
                  className="w-full text-lg"
                  style={{ backgroundColor: primaryColor }}
                >
                  Search Procedures
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}20` }}>
                <Shield className="w-6 h-6" style={{ color: primaryColor }} />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Personalized Estimates</h3>
              <p className="text-sm text-gray-600">Based on your specific insurance plan</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center bg-blue-100">
                <Search className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Provider Comparison</h3>
              <p className="text-sm text-gray-600">Compare costs across facilities</p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center bg-purple-100">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Real-Time Data</h3>
              <p className="text-sm text-gray-600">Up-to-date pricing information</p>
            </div>
          </div>

          {hasSubscription && (
            <div className="mt-8 p-4 bg-green-50 border-l-4 border-green-500 rounded">
              <p className="text-sm text-green-800">
                <strong>Free PDF Downloads:</strong> As a partner of {organization.organization_name}, 
                you can download your cost estimates as PDF for free!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            Cost transparency provided by <span className="text-white font-semibold">Healthcare Dollar</span>
          </p>
          <p className="text-xs mt-2">
            In partnership with {organization.organization_name}
          </p>
        </div>
      </footer>
    </div>
  );
}

// Helper function to adjust color brightness
function adjustColor(color, percent) {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255))
    .toString(16).slice(1);
}
